import { Box, Card, Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import { AppUtils } from "source/framework/appUtils";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import * as riskService from "source/models/services/riskService";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { Dialog } from "source/components/framework/Dialog/Dialog";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { ListSearchFilter } from "source/components/framework/List/ListSearchFilter";
import { getFirstSelectableTreeListItem, ITreeListItem, TreeList } from "source/components/framework/List/TreeList";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";
import { IRiskTemplateEditItem } from "ui/Scripts/source/models/riskTemplate/riskTemplateEditItem";
import { IRiskTemplateNew } from "ui/Scripts/source/models/riskTemplate/riskTemplateNew";
import { RiskTemplateReview } from "./RiskTemplateReview";
import { IdTitleNode, IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";

interface IRiskTemplateNewDialogProps {
    companyId: string;
    title: string;
    onOk: (id: string) => void;
    onCancel: () => void;
}

interface IRiskTemplateNewDialogState extends IRiskTemplateNew {
    listItems: ITreeListItem[];
    orgRiskCategories: IIdTitleNode[];
    riskTemplate: IRiskTemplateEditItem;
    riskCategory: IIdTitleNode;
    selectedId: string;
    filter: string;
    initialized: boolean;
}

export const RiskTemplateNewDialog = (
    props: IRiskTemplateNewDialogProps
) => {
    // const createEmptyRiskTemplateEditItem = () => {
    //     const result = new RiskTemplateEditItem();
    //     result.id = Base.emptyGuid;
    //     result.code = `'${Translations.EmptyRiskTemplate}'`;
    //     return result;
    // };

    const [state, setState] = useState<IRiskTemplateNewDialogState>({
        //systemRiskTemplates: [createEmptyRiskTemplateEditItem()],
        systemRiskTemplates: [],
        companyRiskTemplates: [],
        systemRiskCategories: [],
        companyRiskCategories: [],
        orgRiskCategories: [],
        listItems: [],
        riskTemplate: null,
        riskCategory: null,
        selectedId: Base.emptyGuid,
        filter: "",
        initialized: false,
    });
    
    const riskTemplateEditItemsToTreeListItems = (riskTemplates: IRiskTemplateEditItem[]): ITreeListItem[] => {
        return riskTemplates.map(item => {
            return {
                id: item.id,
                title: item.code,
                children: []
            };
        });
    };

    const getSubTreeListItemsChildren = (riskTemplates: IRiskTemplateEditItem[], riskCategories: IIdTitleNode[]): ITreeListItem[] => {
        const result: ITreeListItem[] = [];
        let unGroupedTreeListItems = riskTemplateEditItemsToTreeListItems(riskTemplates);
        const groups = Base.getUniqueStringItems(riskTemplates.map(rt => rt.riskCategoryId)).filter(g => g).sort();
        if (groups.length < 1) return unGroupedTreeListItems;
        const filteredRiskCategories = IdTitleNode.getAsList(riskCategories).filter(c => groups.some(g => g === c.id));
        for (const category of filteredRiskCategories) {
            const groupRiskTemplates = riskTemplates.filter(rt => rt.riskCategoryId === category.id);
            if (groupRiskTemplates.length < 1) continue;
            const treeListItem = {
                id: category.id,
                title: category.title || category.id,
                children: riskTemplateEditItemsToTreeListItems(groupRiskTemplates)
            };
            result.push(treeListItem);
            unGroupedTreeListItems = unGroupedTreeListItems.filter(i => !groupRiskTemplates.some(g => g.id === i.id));
        }
        if (unGroupedTreeListItems.length > 0) {
            result.push({
                id: Translations.OtherRiskTemplates,
                title: Translations.OtherRiskTemplates,
                children: unGroupedTreeListItems
            });
        }
        return result;
    };

    const getTreeListItems = (allSystemRiskTemplates: IRiskTemplateEditItem[], allCompanyRiskTemplates: IRiskTemplateEditItem[],
        systemRiskCategories: IIdTitleNode[], companyRiskCategories: IIdTitleNode[], searchFilter: string): ITreeListItem[] => {
        // const result: ITreeListItem[] = [];
        const lowSearchFilter = searchFilter.toLocaleLowerCase();
        // Add empty risk template to the top of the list
        // const emptyRiskTemplate = createEmptyRiskTemplateEditItem();
        // if (!lowSearchFilter || emptyRiskTemplate.code.toLocaleLowerCase().indexOf(lowSearchFilter) > -1) {
        //     result.push({ id: emptyRiskTemplate.id, title: emptyRiskTemplate.code, children: [] });
        // }
        // Add company risk templates
        // const companyRiskTemplates = lowSearchFilter
        //     ? allCompanyRiskTemplates.filter(i => i.code.toLocaleLowerCase().indexOf(searchFilter) > -1)
        //     : allCompanyRiskTemplates.slice(0);
        // if (companyRiskTemplates.length > 0) {
        //     result.push({
        //         id: Translations.RiskTemplates,
        //         title: Translations.RiskTemplates,
        //         children: getSubTreeListItemsChildren(companyRiskTemplates, companyRiskCategories)
        //     });
        // }
        // Add system risk templates
        const systemRiskTemplates = lowSearchFilter
            ? allSystemRiskTemplates.filter(i => i.code.toLocaleLowerCase().indexOf(searchFilter) > -1)
            : allSystemRiskTemplates.slice(0);
        return getSubTreeListItemsChildren(systemRiskTemplates, systemRiskCategories);
        // if (systemRiskTemplates.length > 0) {
        //     result.push({
        //         id: Translations.SharedRiskTemplates,
        //         title: Translations.SharedRiskTemplates,
        //         children: getSubTreeListItemsChildren(systemRiskTemplates, systemRiskCategories)
        //     });
        // }
        // return result;
    };

    const getRiskCategory = (riskTemplate: IRiskTemplateEditItem, orgRiskCategories: IIdTitleNode[]) => {
        return riskTemplate
            ? orgRiskCategories.find(c => c.id === riskTemplate.riskCategoryId)
            : (orgRiskCategories.length > 0 ? orgRiskCategories[0] : null);
    };

    const loadItems = async () => {
        const result = await AppUtils.callService(() => riskService.getRiskTemplateNew(props.companyId), false);
        if (!result) {
            props.onCancel();
            return;
        }
        const orgRiskCategories = IdTitleNode.getAsList(result.systemRiskCategories.map(i => new IdTitleNode(i)).concat(result.companyRiskCategories.map(i => new IdTitleNode(i))));
        IdTitleNode.setHierarchialTitles(result.systemRiskCategories);
        IdTitleNode.setHierarchialTitles(result.companyRiskCategories);
        const listItems = getTreeListItems(result.systemRiskTemplates, result.companyRiskTemplates,
            result.systemRiskCategories, result.companyRiskCategories, state.filter);
        const selectedId = getFirstSelectableTreeListItem(listItems, Base.emptyGuid)?.id ?? state.selectedId;
        const riskTemplate = result.systemRiskTemplates.union(result.companyRiskTemplates).find(i => i.id === selectedId) ?? state.riskTemplate;
        setState((old) => ({
            ...old,
            initialized: true,
            systemRiskTemplates: result.systemRiskTemplates,
            companyRiskTemplates: result.companyRiskTemplates,
            systemRiskCategories: result.systemRiskCategories,
            companyRiskCategories: result.companyRiskCategories,
            orgRiskCategories,
            listItems,
            riskTemplate,
            riskCategory: getRiskCategory(riskTemplate, orgRiskCategories),
            selectedId: riskTemplate?.id ?? ""
        }));
    };

    useEffect(() => {
        loadItems();
    }, []);

    const changeFilter = (searchFilter: string) => {
        let systemRiskTemplates: IRiskTemplateEditItem[] = [];
        let companyRiskTemplates: IRiskTemplateEditItem[] = [];
        if (searchFilter) {
            const lowSearchFilter = searchFilter.toLocaleLowerCase();
            systemRiskTemplates = state.systemRiskTemplates.filter(i => i.code.toLocaleLowerCase().indexOf(lowSearchFilter) > -1);
            companyRiskTemplates = state.companyRiskTemplates.filter(i => i.code.toLocaleLowerCase().indexOf(lowSearchFilter) > -1);
        } else {
            systemRiskTemplates = state.systemRiskTemplates.slice(0);
            companyRiskTemplates = state.companyRiskTemplates.slice(0);
        }
        const riskTemplate = systemRiskTemplates.concat(companyRiskTemplates).find(i => i.id === state.selectedId);
        setState((old) => ({
            ...old,
            filter: searchFilter,
            riskTemplate,
            riskCategory: getRiskCategory(riskTemplate, state.orgRiskCategories),
            selectedId: riskTemplate ? state.selectedId : "",
            listItems: getTreeListItems(state.systemRiskTemplates, state.companyRiskTemplates,
                state.systemRiskCategories, state.companyRiskCategories, searchFilter)
        }));
    };

    const handleSetSelectedId = (selectedId: string) => {
        const riskTemplate = state.systemRiskTemplates.concat(state.companyRiskTemplates).find(i => i.id === selectedId);
        setState((old) => ({
            ...old,
            selectedId,
            riskTemplate,
            riskCategory: getRiskCategory(riskTemplate, state.orgRiskCategories),
        }));
    };

    return (
        <Dialog
            id="riskTemplateNewDialog"
            maxWidth="xl"
            maxHeight="100%"
            title={props.title}
            body={
                <GridRow height="100%" sx={{ height: "calc(100% + 16px)" }}>
                    <Grid item sm={4} height="100%">
                        <VerticalContainer
                            before={
                                <ActionContainer
                                    leftItems={
                                        <ListSearchFilter
                                            searchFilter={state.filter}
                                            onSearchClick={changeFilter}
                                        />
                                    }
                                />
                            }
                            main={
                                <Card
                                    elevation={0}
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "divider",
                                        height: "100%",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            borderRadius: ".5rem",
                                            height: "100%",
                                            overflowY: "auto"
                                        }}
                                    >
                                        <TreeList
                                            items={state.listItems}
                                            selectedId={state.selectedId}
                                            onSelect={item => handleSetSelectedId(item.id)}
                                        />
                                    </Box>
                                </Card>
                            }
                        />
                    </Grid>
                    <Grid item sm={8} height="100%">
                        <RiskTemplateReview
                            riskTemplate={state.riskTemplate}
                            riskCategory={state.riskCategory}
                        />
                    </Grid>
                </GridRow>
            }
            actions={
                <DialogButtons
                    saveDisabled={!state.selectedId}
                    saveTitle={Translations.Next}
                    onCancel={props.onCancel}
                    onSave={() => props.onOk(state.selectedId)}
                />
            }
            onClose={props.onCancel}
        />
    );
};
